<template>
  <div>
    <!-- Hero: one input, one promise -->
    <section class="relative bg-hero-fade pt-20 pb-16 lg:pt-28">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>
      <div class="relative container-wide max-w-3xl text-center">
        <div class="eyebrow mx-auto w-fit mb-4">Free AI-search visibility check</div>
        <h1 class="font-display text-5xl lg:text-6xl text-ink-900 leading-[1.05] text-balance">
          Can AI answer engines<br /><span class="italic text-brand-700">find and cite your business?</span>
        </h1>
        <p class="mt-6 text-lg text-ink-600 max-w-2xl mx-auto text-pretty">
          Your customers are asking ChatGPT, Perplexity and Google's AI Overviews for recommendations right now.
          Enter your website and see, in about three minutes, whether those engines can actually read, understand and quote you.
        </p>

        <!-- The checker itself -->
        <form class="mt-9 max-w-xl mx-auto" @submit.prevent="runCheck">
          <div class="flex flex-col sm:flex-row gap-2.5">
            <label class="sr-only" for="geo-checker-url">Your website address</label>
            <input
              id="geo-checker-url"
              v-model="url"
              type="text"
              inputmode="url"
              autocomplete="url"
              placeholder="yourbusiness.com.au"
              class="input flex-1"
              :disabled="submitting"
              @keyup.enter="runCheck"
            />
            <button type="submit" class="btn-brand btn-lg whitespace-nowrap" :disabled="submitting || !url.trim()">
              {{ submitting ? 'Starting…' : 'Run my free check' }}
              <Icon v-if="!submitting" name="arrow-right" :size="16" />
            </button>
          </div>
          <p v-if="error" class="mt-3 text-sm text-danger-600" role="alert">{{ error }}</p>
          <p v-else class="mt-3 text-xs text-ink-500">
            No signup. No credit card. Full report in ~3 minutes.
          </p>
        </form>
      </div>
    </section>

    <!-- What you get -->
    <section class="py-20 lg:py-24 border-b border-ink-100">
      <div class="container-wide max-w-5xl">
        <div class="max-w-2xl mb-10">
          <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-700 bg-brand-50 px-3 py-1 rounded-full border border-brand-100 mb-4">
            <Icon name="sparkles" :size="12" /> What you get
          </div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">A real report, not a teaser score.</h2>
          <p class="mt-4 text-ink-600 text-lg">
            Most "free SEO checkers" hand you one number and hide the rest behind an email wall.
            This one gives you the full report first — you can save it by email if you want to keep it.
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div v-for="c in cards" :key="c.title" class="card card-hover p-6 lg:p-7">
            <span class="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mb-4">
              <Icon :name="c.icon" :size="18" />
            </span>
            <h3 class="font-semibold text-ink-900 mb-2">{{ c.title }}</h3>
            <p class="text-sm text-ink-600 leading-relaxed">{{ c.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why it matters -->
    <section class="py-20 lg:py-24 border-b border-ink-100 bg-ink-50/40">
      <div class="container-wide max-w-3xl">
        <h2 class="font-display text-3xl lg:text-4xl text-ink-900 leading-tight text-balance mb-6">
          Ranking #1 is no longer the finish line.
        </h2>
        <p class="text-ink-600 text-lg leading-relaxed mb-4">
          A growing share of searches now end inside an AI-generated answer, not a list of ten blue links.
          When someone asks ChatGPT "who's the best option for X near me," the businesses named in that answer
          get the customer — and the ones that aren't, don't. You can be invisible in that answer and still rank well
          in Google, which is why a traditional SEO score alone won't tell you what you need to know.
        </p>
        <p class="text-ink-600 text-lg leading-relaxed">
          This check looks at the signals that decide whether an AI engine can extract facts from your pages and cite you:
          whether the crawlers are even allowed in, how quotable and well-structured your content is, and whether your
          structured data says anything an engine can trust.
          <router-link to="/geo-optimization" class="text-brand-700 underline decoration-brand-200 hover:decoration-brand-500 underline-offset-2">Read the full GEO breakdown →</router-link>
        </p>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-20 lg:py-24">
      <div class="container-wide max-w-3xl">
        <h2 class="font-display text-3xl lg:text-4xl text-ink-900 leading-tight mb-8">Questions</h2>
        <div class="space-y-4">
          <div v-for="f in faqs" :key="f.question" class="card p-6">
            <h3 class="font-semibold text-ink-900 mb-2">{{ f.question }}</h3>
            <p class="text-sm text-ink-600 leading-relaxed">{{ f.answer }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom CTA -->
    <section class="py-16 bg-ink-900 text-white">
      <div class="container-wide max-w-3xl text-center">
        <!-- Real bug (owner report 2026-09-18): the heading was invisible --
             black on near-black. The section sets `text-white`, but that only
             *inherits*; the base-layer `h1..h6 { color: ink-900 }` rule is a
             declaration on the element itself and beats inheritance every
             time. Any dark section has to colour its headings explicitly. -->
        <h2 class="font-display text-3xl lg:text-4xl leading-tight text-balance text-white">See where you stand in AI search.</h2>
        <p class="mt-4 text-ink-300 text-lg">It takes three minutes and costs nothing.</p>
        <div class="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <router-link to="/analyze" class="btn-brand btn-lg btn-glow">Run a free audit <Icon name="arrow-right" :size="16" /></router-link>
          <!-- Secondary on a dark surface: not `.btn-outline`, whose white fill
               + forced white label made this a white-on-white blank pill
               (same owner report). Transparent fill, ink border, white label. -->
          <router-link to="/pricing" class="btn btn-lg border border-ink-600 text-white hover:bg-ink-800 hover:border-ink-500">See plans</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { usePageMeta } from '../../composables/usePageMeta.js';
import { trackAuditStarted } from '../../analytics/events.js';
import Icon from '../ui/Icon.vue';

usePageMeta({
  // Kept in sync byte-for-byte with core/page_meta.py's STATIC_PAGE_META
  // entry for /geo-checker — the server injects that copy into the raw
  // HTML for non-JS crawlers, and tests/regression/test_meta_description_
  // lengths.py fails if the two ever drift. No apostrophes that would need
  // escaping in the .vue source (that exact trap bit /about once).
  title: 'Free AI-Search (GEO) Checker — LeadRanks',
  meta: [
    { name: 'description', content: 'Free AI-search (GEO) check: enter any website and see, in about three minutes, whether ChatGPT, Perplexity and Google AI Overviews can find and cite it.' },
    { property: 'og:title', content: 'Free AI-Search (GEO) Checker — LeadRanks' },
    { property: 'og:description', content: 'Free AI-search (GEO) check: enter any website and see, in about three minutes, whether ChatGPT, Perplexity and Google AI Overviews can find and cite it.' },
  ],
});

const url = ref('');
const submitting = ref(false);
const error = ref('');

// Only the host is sent to GA4 — never the full URL, and never the address
// a visitor typed beyond the domain. Matches AnalyzePage's own helper, so
// audit_started reads the same in GA4 whichever entry point was used.
const domainForAnalytics = (input) => {
  try {
    const u = new URL(String(input).includes('://') ? String(input) : `https://${input}`);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const runCheck = async () => {
  const value = url.value.trim();
  if (!value || submitting.value) return;
  submitting.value = true;
  error.value = '';

  // Same endpoint and same guest funnel as the headline audit box — this
  // page only changes the *framing* of the ask, not the machinery. mode
  // 'standard' and the visitor's saved region keep it identical to what
  // /analyze would have submitted from the same browser.
  const region = (typeof localStorage !== 'undefined' && localStorage.getItem('preferred_region')) || 'us';

  try {
    const response = await window.fetchWithCSRF('/api/public/analyze', {
      method: 'POST',
      body: JSON.stringify({ url: value, mode: 'standard', region }),
    });
    const data = await response.json().catch(() => ({}));

    if (data.success && data.analysis_id) {
      trackAuditStarted({ domain: domainForAnalytics(value), mode: 'standard' });
      localStorage.setItem('pending_analysis_id', data.analysis_id);
      setTimeout(() => {
        window.location.href = `/processing/${data.analysis_id}`;
      }, 400);
      return;
    }

    if (response.status === 429 || data.retry_after) {
      error.value = data.error || 'Too many checks from this network right now — please try again a little later.';
    } else {
      error.value = data.error || 'Something went wrong starting your check. Please try again.';
    }
  } catch (e) {
    error.value = 'Network error — please try again.';
  } finally {
    submitting.value = false;
  }
};

const cards = [
  {
    icon: 'cpu',
    title: 'AI-search (GEO) score',
    body: 'A 0–100 score for how well AI answer engines can find, read and quote your pages — broken into the categories that actually move the number.',
  },
  {
    icon: 'search',
    title: 'Technical + content checks',
    body: 'Crawler access, robots and sitemap, titles and metadata, content depth and structure, Core Web Vitals, schema — the same 100+ point audit paying customers get.',
  },
  {
    icon: 'list',
    title: 'A prioritised action plan',
    body: 'Not a wall of raw data. The specific fixes, ordered by what will make the biggest difference first — with the reasoning for each.',
  },
];

const faqs = [
  {
    question: 'Is this really free?',
    answer: 'Yes. There is no signup, no credit card, and no email required to see your report. If you want to keep the report, you can have a link emailed to you — that is the only step that asks for anything.',
  },
  {
    question: 'How long does it take?',
    answer: 'Usually about three minutes. You enter your address, we scan the live page, and the report opens when it is ready.',
  },
  {
    question: 'What is GEO, and how is it different from SEO?',
    answer: 'SEO earns you a position in a list of results a human clicks through. GEO (Generative Engine Optimization) earns you a place inside an AI-generated answer the reader may never click past. The signals overlap, but GEO also covers things SEO tools usually ignore — whether AI crawlers are allowed in, how quotable your content is, and whether your structured data is trustworthy.',
  },
  {
    question: 'Can I check a competitor?',
    answer: 'Yes — enter any public website. People often run their own site and two or three competitors to see the gap.',
  },
  {
    question: 'Do you store the address I enter or email me without asking?',
    answer: 'We only email a report link if you ask for one, and that address is added to our newsletter list as a pending, double opt-in subscriber — you confirm before you ever receive anything else. See our privacy policy for details.',
  },
];
</script>
